import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { CircleCheck } from 'lucide-react';
import { forgotPasswordSchema } from '@sf/shared';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { t } from '../../lib/copy';
import { messageForError } from '../../lib/errors';
import { resetPassword } from './api';

export function ForgotPasswordPage() {
  const [sent, setSent] = useState(false);
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<{ email: string }>({ resolver: zodResolver(forgotPasswordSchema) });

  const onSubmit = handleSubmit(async ({ email }) => {
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err) {
      toast({ title: messageForError(err), variant: 'error' });
    }
  });

  return (
    <div>
      <h1 className="text-2xl font-bold">Reset your password</h1>
      {sent ? (
        <div
          role="status"
          className="mt-6 flex gap-3 rounded-card border border-valley/30 bg-valley-50 p-4 text-sm text-valley-700"
        >
          <CircleCheck className="size-5 shrink-0" aria-hidden />
          <p>{t('auth.reset.sent')}</p>
        </div>
      ) : (
        <>
          <p className="mt-1 text-sm text-slate">
            Enter your email and we'll send you a link to choose a new password.
          </p>
          <form onSubmit={onSubmit} noValidate className="mt-6 flex flex-col gap-4">
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              error={errors.email?.message}
              {...register('email')}
            />
            <Button type="submit" loading={isSubmitting} className="w-full justify-center">
              Send reset link
            </Button>
          </form>
        </>
      )}
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-medium text-brand underline-offset-2 hover:underline">
          Back to sign in
        </Link>
      </p>
    </div>
  );
}
